(() => {
  if (customElements.get('bbb-card-quick-add')) return;

  let cartPromise;
  let cartReadAt = 0;
  const quoteRequests = new Map();
  const cardMarkupRequests = new Map();
  const cacheTtl = 10000;
  const root = window.Shopify?.routes?.root || '/';
  const booklistCollectionHandle = 'battle-of-the-books-2026-2027';
  const booklistCacheKey = 'bbb-booklist-card-handles-v2';
  const request = async (url) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}`);
        error.retryable = response.status === 429 || response.status === 408 || response.status >= 500;
        const body = await response.json().catch(() => ({}));
        const retryAfter = response.headers.get('Retry-After');
        const seconds = Number(body?.retryAfterSeconds || retryAfter);
        error.retryAfterMs = Number.isFinite(seconds) && seconds > 0
          ? seconds * 1000
          : Math.max(0, Date.parse(retryAfter) - Date.now()) || 0;
        throw error;
      }
      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  };
  const getCart = () => {
    if (!cartPromise || Date.now() - cartReadAt > 10000) {
      cartReadAt = Date.now();
      cartPromise = request(`${root}cart.js`).catch((error) => {
        cartPromise = null;
        throw error;
      });
    }
    return cartPromise;
  };
  const getQuote = async (variantId, quantity) => {
    const cart = await getCart();
    const inCart = (cart.items || []).reduce((sum, item) => sum + (String(item.variant_id) === variantId ? item.quantity : 0), 0);
    const total = inCart + quantity;
    const configured = window.BBB_ETACartWriterConfig?.appProxyPath || '/apps/bbb-eta-lab/variants';
    const url = new URL(configured, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid ETA endpoint');
    url.pathname = url.pathname.replace(/\/variants\/?$/, '/quote');
    url.searchParams.set('variant_id', variantId);
    url.searchParams.set('requested_total', String(total));
    const key = url.href;
    for (const [cachedKey, entry] of quoteRequests) {
      if (entry.expires <= Date.now()) quoteRequests.delete(cachedKey);
    }
    let entry = quoteRequests.get(key);
    if (!entry) {
      entry = { expires: Infinity };
      entry.promise = request(url).then((result) => {
        const quote = result?.quote;
        if (!result.ok || String(quote?.variantId) !== variantId || quote.requestedTotal !== total || typeof quote.canAdd !== 'boolean') throw new Error('Invalid ETA quote');
        entry.expires = Date.now() + cacheTtl;
        return quote;
      }).catch((error) => {
        if (quoteRequests.get(key) === entry) quoteRequests.delete(key);
        throw error;
      });
      quoteRequests.set(key, entry);
      if (quoteRequests.size > 96) quoteRequests.delete(quoteRequests.keys().next().value);
    }
    return { quote: await entry.promise, inCart, expires: entry.expires };
  };
  const formatDate = (iso) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return '';
    const date = new Date(`${iso}T12:00:00Z`);
    if (!Number.isFinite(date.getTime())) return '';
    return `${iso.slice(8)}-${new Intl.DateTimeFormat('en', { month: 'short', timeZone: 'UTC' }).format(date)}-${iso.slice(0, 4)}`;
  };

  let booklistHandlesPromise;
  const getBooklistHandles = async () => {
    if (booklistHandlesPromise) return booklistHandlesPromise;
    booklistHandlesPromise = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(booklistCacheKey) || 'null');
        if (cached?.expires > Date.now() && Array.isArray(cached.handles)) return new Set(cached.handles);
      } catch (error) {}

      const url = `${root}collections/${booklistCollectionHandle}?view=bbb-card-membership`;
      const payload = await request(url);
      const handles = (payload?.handles || []).filter(Boolean);
      try {
        sessionStorage.setItem(booklistCacheKey, JSON.stringify({ handles, expires: Date.now() + 300000 }));
      } catch (error) {}
      return new Set(handles);
    })().catch((error) => {
      booklistHandlesPromise = null;
      throw error;
    });
    return booklistHandlesPromise;
  };

  const getCardHandle = (card) => {
    const link = card.querySelector('.product-item__title[href], .product-item__image-wrapper[href]');
    if (!link) return '';
    try {
      const path = new URL(link.href, location.origin).pathname;
      const match = path.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  };

  const getCardMarkup = (handle) => {
    if (cardMarkupRequests.has(handle)) return cardMarkupRequests.get(handle);
    const url = new URL(`${root}products/${encodeURIComponent(handle)}`, location.origin);
    url.searchParams.set('section_id', 'bbb-card-quick-add');
    const promise = fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const quickAdd = doc.querySelector('bbb-card-quick-add');
        if (!quickAdd) throw new Error('Missing card variant selector');
        return quickAdd;
      })
      .catch((error) => {
        cardMarkupRequests.delete(handle);
        throw error;
      });
    cardMarkupRequests.set(handle, promise);
    return promise;
  };

  const mountBooklistSearchCard = async (card, handles) => {
    if (!card.isConnected || card.matches('.product-item--horizontal, .product-item--complementary') || card.querySelector('bbb-card-quick-add')) return;
    const handle = getCardHandle(card);
    if (!handle || !handles.has(handle)) return;
    const info = card.querySelector('.product-item__info');
    if (!info) return;
    card.dataset.bbbBooklistHydrating = '1';
    try {
      const quickAdd = (await getCardMarkup(handle)).cloneNode(true);
      if (!card.isConnected || card.querySelector('bbb-card-quick-add')) return;
      card.querySelectorAll('.meta-results__product-commerce, .product-item__price-list, .product-item__inventory, .product-item__action-list').forEach((element) => element.remove());
      card.setAttribute('data-bbb-quick-add-card', '');
      info.appendChild(quickAdd);
      card.dataset.bbbBooklistHydrated = '1';
    } catch (error) {
      delete card.dataset.bbbBooklistHydrating;
    }
  };

  let searchHydrationQueued = false;
  const hydrateBooklistSearchCards = (scope = document) => {
    if (!document.body?.classList.contains('template-search') && !document.querySelector('.meta-results')) return;
    if (searchHydrationQueued) return;
    searchHydrationQueued = true;
    queueMicrotask(async () => {
      searchHydrationQueued = false;
      try {
        const handles = await getBooklistHandles();
        const cards = [];
        if (scope instanceof Element && scope.matches('.product-item')) cards.push(scope);
        scope.querySelectorAll?.('.product-item').forEach((card) => cards.push(card));
        cards.forEach((card) => {
          if (card.dataset.bbbBooklistHydrated === '1' || card.dataset.bbbBooklistHydrating === '1') return;
          mountBooklistSearchCard(card, handles);
        });
      } catch (error) {}
    });
  };
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      entry.target.inViewport = entry.isIntersecting;
      if (entry.isIntersecting) entry.target.refreshQuote().catch(() => {});
      else clearTimeout(entry.target.quoteRetryTimer);
    }
  }, { rootMargin: '0px' });

  class CardQuickAdd extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-value]')) this.selectVariant(event.target);
      }, options);
      this.addEventListener('click', (event) => this.beforeAdd(event), { ...options, capture: true });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-swatch-scroll]');
        if (!button || button.disabled) return;
        event.preventDefault();
        event.stopPropagation();
        const track = button.closest('.bbb-quick-add__swatch-strip').querySelector('.bbb-quick-add__thumbnails');
        const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
        const thumbnail = track.querySelector('.bbb-quick-add__thumbnail');
        const step = thumbnail.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
        track.scrollBy({ left: Number(button.dataset.swatchScroll) * direction * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, options);
      this.addEventListener('submit', (event) => {
        event.preventDefault();
        this.querySelector('button[type="submit"]:not(:disabled)')?.click();
      }, options);
      this.selections = new Map();
      this.cacheSelection(this.selectedOptions().join(','), this.outerHTML);
      this.readState();
      this.syncImage();
      this.setAttribute('data-ready', '');
      observer.observe(this);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.selectionRequest?.abort();
      this.swatchEvents?.abort();
      this.swatchResize?.disconnect();
      clearTimeout(this.quoteRetryTimer);
      observer.unobserve(this);
      this.revision = (this.revision || 0) + 1;
    }

    readState() {
      this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
      this.form = this.querySelector('form');
      this.button = this.form.querySelector('button[type="submit"]');
      this.nativeButton = { disabled: this.button.disabled, text: this.button.textContent, className: this.button.className };
      this.nativeStock = this.querySelector('[data-stock]').innerHTML;
      this.renderedOptions = this.selectedOptions();
      this.setupSwatchScroll();
      if (this.querySelector('.block-swatch__item-status--on-order')) {
        this.querySelector('[data-stock]').textContent = `${this.copy.dispatch} ...`;
      }
      this.invalidateQuote(true);
    }

    cacheSelection(key, html) {
      this.selections.set(key, { html, expires: Date.now() + cacheTtl });
      if (this.selections.size > 8) this.selections.delete(this.selections.keys().next().value);
    }

    setupSwatchScroll() {
      this.swatchEvents?.abort();
      this.swatchResize?.disconnect();
      this.swatchEvents = new AbortController();
      const strips = Array.from(this.querySelectorAll('.bbb-quick-add__swatch-strip'));
      if (!strips.length) return;
      const update = () => {
        for (const strip of strips) {
          const track = strip.querySelector('.bbb-quick-add__thumbnails');
          const previous = strip.querySelector('[data-swatch-scroll="-1"]');
          const next = strip.querySelector('[data-swatch-scroll="1"]');
          const overflow = track.scrollWidth > strip.clientWidth + 1;
          strip.dataset.overflow = String(overflow);
          previous.hidden = next.hidden = !overflow;
          const position = Math.abs(track.scrollLeft);
          previous.disabled = position < 1;
          next.disabled = position >= track.scrollWidth - track.clientWidth - 1;
        }
      };
      this.swatchResize = new ResizeObserver(update);
      for (const strip of strips) {
        this.swatchResize.observe(strip);
        this.swatchResize.observe(strip.querySelector('.bbb-quick-add__thumbnails'));
        strip.querySelector('.bbb-quick-add__thumbnails').addEventListener('scroll', update, { passive: true, signal: this.swatchEvents.signal });
      }
      update();
    }

    invalidateQuote(resetRetry = false) {
      clearTimeout(this.quoteRetryTimer);
      if (resetRetry) {
        this.quoteRetryAt = 0;
        this.quoteRetryAttempts = 0;
      }
      this.quoteEpoch = (this.quoteEpoch || 0) + 1;
      this.quote = null;
      this.quoteAt = 0;
      this.quotePromise = null;
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('select[data-option-value], input[data-option-value]:checked, input[type="hidden"][data-option-value]'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((input) => input.value);
    }

    async selectVariant(control) {
      this.selectionRequest?.abort();
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(new Error('Variant request timed out')), 10000);
      this.selectionRequest = controller;
      const revision = this.revision = (this.revision || 0) + 1;
      const selected = this.selectedOptions();
      const focus = { position: control.dataset.optionPosition, value: control.value };
      const option = control.tagName === 'SELECT' ? control.selectedOptions[0] : control;
      const changedOptions = selected.filter((value, index) => value !== this.renderedOptions[index]).length;
      const prefetch = setTimeout(() => {
        if (!controller.signal.aborted && revision === this.revision && changedOptions === 1 && option.dataset.variantId) {
          getQuote(option.dataset.variantId, Number(this.form.elements.quantity.value || 1)).catch(() => {});
        }
      }, 75);
      this.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
      this.querySelector('[data-eta]').textContent = '';
      this.showError('');
      const url = new URL(this.dataset.productUrl, location.origin);
      url.searchParams.set('section_id', 'bbb-card-quick-add');
      url.searchParams.set('option_values', selected.join(','));
      try {
        const key = selected.join(',');
        const cached = this.selections.get(key);
        let html = cached?.expires > Date.now() ? cached.html : null;
        if (!html) {
          const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) throw new Error('Variant request failed');
          html = await response.text();
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('bbb-card-quick-add');
        if (!next || next.dataset.productId !== this.dataset.productId) throw new Error('Invalid variant response');
        const returned = Array.from(next.querySelectorAll('select[data-option-value], input[data-option-value]:checked, input[type="hidden"][data-option-value]'))
          .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
          .map((input) => input.value);
        if (returned.join(',') !== selected.join(',')) throw new Error('Selection mismatch');
        if (revision !== this.revision || !this.isConnected) return;
        if (html !== cached?.html) this.cacheSelection(key, next.outerHTML);
        const oldFormId = this.form.id;
        next.querySelector('form').id = oldFormId;
        next.querySelectorAll('input[data-option-value]').forEach((input) => {
          input.name = `${oldFormId}-option-${input.dataset.optionPosition}`;
        });
        const restoreFocus = this.contains(document.activeElement);
        this.dataset.variantId = next.dataset.variantId;
        this.dataset.variantUrl = next.dataset.variantUrl;
        this.dataset.bigSize = next.dataset.bigSize;
        this.replaceChildren(...next.childNodes);
        this.readState();
        this.syncImage();
        if (restoreFocus) {
          const input = Array.from(this.querySelectorAll('[data-option-value]')).find((item) =>
            item.dataset.optionPosition === focus.position && (item.tagName === 'SELECT' || item.value === focus.value));
          input?.focus({ preventScroll: true });
          input?.closest('.bbb-quick-add__thumbnail')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
        this.removeAttribute('aria-busy');
        this.refreshQuote().catch(() => {});
      } catch (error) {
        if (revision !== this.revision || !this.isConnected) return;
        this.removeAttribute('aria-busy');
        this.showError(this.copy.load_error);
        this.button.disabled = true;
      } finally {
        clearTimeout(timeout);
        clearTimeout(prefetch);
      }
    }

    syncImage() {
      const card = this.closest('.product-item');
      if (!card) return;
      card.querySelectorAll('[data-bbb-big-book-badge][data-context="card"]').forEach((badge) => {
        badge.hidden = this.dataset.bigSize !== 'true';
        badge.dataset.variantId = this.dataset.variantId;
      });
      const next = this.querySelector('[data-card-image]')?.content.querySelector('img');
      const previous = card.querySelector('.product-item__primary-image');
      if (next && previous && next.src !== previous.src) previous.replaceWith(next.cloneNode(true));
      card.querySelectorAll('.product-item__image-wrapper, .product-item__title').forEach((link) => {
        link.href = this.dataset.variantUrl;
      });
    }

    showError(message) {
      const error = this.querySelector('[data-error]');
      error.textContent = message;
      error.hidden = !message;
      this.querySelector('[data-details]').hidden = !message;
    }

    async refreshQuote(force = false) {
      if (!this.dataset.variantId || this.hasAttribute('aria-busy')) return null;
      if (!force && this.quote && Date.now() < this.quoteExpires) return this.quote;
      if (this.quotePromise) return this.quotePromise;
      if (this.quoteRetryAttempts > 3) return null;
      if (Date.now() < this.quoteRetryAt) {
        this.scheduleQuoteRetry();
        return null;
      }
      const revision = this.revision || 0;
      const epoch = this.quoteEpoch;
      const variantId = this.dataset.variantId;
      this.quotePromise = (async () => {
        const { quote, inCart, expires } = await getQuote(variantId, Number(this.form.elements.quantity.value || 1));
        if (revision !== (this.revision || 0) || epoch !== this.quoteEpoch || !this.isConnected) return null;
        this.quote = quote;
        this.quoteAt = Date.now();
        this.quoteExpires = expires;
        clearTimeout(this.quoteRetryTimer);
        this.quoteRetryAt = 0;
        this.quoteRetryAttempts = 0;
        this.applyQuote(quote, inCart);
        this.showError('');
        return quote;
      })().catch((error) => {
        if (revision === (this.revision || 0) && epoch === this.quoteEpoch && this.isConnected) {
          this.querySelector('[data-eta]').textContent = this.copy.eta_unavailable;
          if (error.retryable !== false) {
            const attempts = this.quoteRetryAttempts || 0;
            this.quoteRetryAttempts = attempts + 1;
            this.quoteRetryAt = Date.now() + Math.max(error.retryAfterMs || 0, [1500, 4000, 10000][Math.min(attempts, 2)]);
            this.scheduleQuoteRetry();
          }
        }
        throw error;
      }).finally(() => {
        if (revision === (this.revision || 0) && epoch === this.quoteEpoch) this.quotePromise = null;
      });
      return this.quotePromise;
    }

    scheduleQuoteRetry() {
      clearTimeout(this.quoteRetryTimer);
      if (!this.isConnected || !this.inViewport || this.quoteRetryAttempts > 3) return;
      this.quoteRetryTimer = setTimeout(() => {
        if (this.isConnected && this.inViewport) this.refreshQuote().catch(() => {});
      }, Math.max(0, this.quoteRetryAt - Date.now()));
    }

    applyQuote(quote, inCart) {
      const date = formatDate(quote.dispatchIso);
      const dispatch = date ? `${this.copy.dispatch} ${date}` : '';
      const stock = this.querySelector('[data-stock]');
      stock.innerHTML = this.nativeStock;
      if (quote.status === 'in_stock') {
        const label = stock.firstElementChild;
        if (label) label.textContent = this.copy.in_stock;
        else stock.textContent = this.copy.in_stock;
      }
      if (quote.status === 'on_order' || quote.status === 'partially_on_order') stock.textContent = dispatch || this.copy.eta_unavailable;
      if (quote.status === 'out_of_stock') stock.textContent = this.copy.out_of_stock;
      this.button.textContent = this.nativeButton.text;
      this.button.className = this.nativeButton.className;
      this.button.disabled = this.nativeButton.disabled || !quote.canAdd;
      if (!quote.canAdd) {
        this.button.textContent = inCart ? this.copy.limit_reached : this.copy.out_of_stock;
        this.button.className = 'product-form__add-button button button--disabled';
      } else if (quote.preorder && !this.nativeButton.disabled) {
        this.button.textContent = this.dataset.preorderLabel;
        this.button.className = 'product-form__add-button button button--secondary';
      }
      this.form.querySelectorAll('[name="properties[_p]"], [name="properties[_r]"], [name="properties[_ni]"]').forEach((input) => input.remove());
      this.form.removeAttribute('data-bbb-eta-quote-variant-id');
      if (quote.preorder) {
        const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Hong_Kong', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
        const release = formatDate(quote.releaseIso);
        const properties = {
          _p: dispatch,
          _r: quote.releaseIso > today && release ? `${this.copy.release} ${release}` : '',
          _ni: quote.nativeIncomingIso || ''
        };
        for (const [key, value] of Object.entries(properties)) {
          if (!value) continue;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = value;
          input.setAttribute('data-card-snapshot', '');
          this.form.append(input);
        }
        this.form.setAttribute('data-bbb-eta-quote-variant-id', this.dataset.variantId);
      }
      this.dispatchEvent(new CustomEvent('bbb:card-quote', { bubbles: true }));
    }

    async beforeAdd(event) {
      if (!event.target.closest('button[type="submit"]') || this.allowAdd) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.hasAttribute('aria-busy') || this.addPending) return;
      const revision = this.revision || 0;
      this.addPending = true;
      this.button.disabled = true;
      try {
        if (this.quoteRetryAttempts > 3 && Date.now() >= this.quoteRetryAt) this.quoteRetryAttempts = 0;
        const quote = await this.refreshQuote();
        if (revision !== (this.revision || 0) || !this.isConnected) return;
        if (!quote) {
          this.button.disabled = this.nativeButton.disabled || this.quote?.canAdd === false;
          return;
        }
        this.button.disabled = this.nativeButton.disabled || !quote.canAdd;
        if (this.button.disabled) return;
        this.allowAdd = true;
        this.button.click();
        this.allowAdd = false;
      } catch (error) {
        if (revision === (this.revision || 0)) {
          this.button.disabled = this.nativeButton.disabled;
          this.showError(this.copy.eta_unavailable);
        }
      } finally {
        this.addPending = false;
      }
    }
  }

  for (const name of ['product:added', 'cart:refresh', 'cart:updated', 'cart:rerendered']) {
    document.addEventListener(name, () => {
      cartPromise = null;
      quoteRequests.clear();
      document.querySelectorAll('bbb-card-quick-add').forEach((card) => {
        if (typeof card.invalidateQuote !== 'function') return;
        card.invalidateQuote();
        if (card.inViewport) card.refreshQuote(true).catch(() => {});
      });
    }, { capture: true });
  }
  customElements.whenDefined('buy-button').then(() => {
    if (!customElements.get('bbb-card-quick-add')) customElements.define('bbb-card-quick-add', CardQuickAdd);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => hydrateBooklistSearchCards());
  } else {
    hydrateBooklistSearchCards();
  }
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element && (node.matches('.product-item, .meta-results') || node.querySelector('.product-item'))) {
          hydrateBooklistSearchCards(node);
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

